import React, { createContext, useContext, useState, useEffect } from 'react'
import type { ReactNode } from 'react'
import type { ColorPalette, ColorPaletteKey } from '../styles/colorPalettes'
import { colorPalettes, defaultPalette } from '../styles/colorPalettes'

interface ColorThemeContextType {
  palette: ColorPalette
  paletteKey: ColorPaletteKey
  setPalette: (key: ColorPaletteKey) => void
}

const ColorThemeContext = createContext<ColorThemeContextType | undefined>(undefined)

// カラーテーマプロバイダー
export const ColorThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [paletteKey, setPaletteKey] = useState<ColorPaletteKey>(() => {
    // ページに応じてデフォルトテーマを決定
    const currentPath = window.location.pathname
    if (currentPath.includes('/landing') || currentPath === '/') {
      return 'evergreenIvory' // LPページはEvergreenを使用
    }
    // その他のアプリページもEvergreenをデフォルトに
    const saved = localStorage.getItem('evaloidColorPalette') as ColorPaletteKey | null
    return saved && saved in colorPalettes ? saved : 'evergreenIvory'
  })

  const palette = colorPalettes[paletteKey]

  const setPalette = (key: ColorPaletteKey) => {
    setPaletteKey(key)
    localStorage.setItem('evaloidColorPalette', key)
  }

  // ルート変更を監視してテーマを自動切り替え
  useEffect(() => {
    const handleRouteChange = () => {
      const currentPath = window.location.pathname
      if (currentPath.includes('/landing') || currentPath === '/') {
        setPaletteKey('evergreenIvory')
      } else {
        // アプリページでは保存されたテーマか、デフォルトのevergreenIvoryを使用
        const saved = localStorage.getItem('evaloidColorPalette') as ColorPaletteKey | null
        const appPalette = saved && saved in colorPalettes ? saved : 'evergreenIvory'
        setPaletteKey(appPalette)
      }
    }

    // popstate（戻る/進む）とpushstate（プログラムによる遷移）を監視
    window.addEventListener('popstate', handleRouteChange)
    
    // React Routerのナビゲーション用
    const originalPushState = history.pushState
    const originalReplaceState = history.replaceState
    
    history.pushState = function(...args) {
      originalPushState.apply(history, args)
      setTimeout(handleRouteChange, 0)
    }
    
    history.replaceState = function(...args) {
      originalReplaceState.apply(history, args)
      setTimeout(handleRouteChange, 0)
    }

    return () => {
      window.removeEventListener('popstate', handleRouteChange)
      history.pushState = originalPushState
      history.replaceState = originalReplaceState
    }
  }, [])

  // CSS変数として色を適用
  useEffect(() => {
    const root = document.documentElement
    const colors = palette.colors

    // Primary colors
    root.style.setProperty('--color-primary', colors.primary)
    root.style.setProperty('--color-primary-light', colors.primaryLight)
    root.style.setProperty('--color-primary-dark', colors.primaryDark)
    
    // Secondary colors
    root.style.setProperty('--color-secondary', colors.secondary)
    root.style.setProperty('--color-secondary-light', colors.secondaryLight)
    root.style.setProperty('--color-secondary-dark', colors.secondaryDark)
    
    // Accent colors
    root.style.setProperty('--color-accent', colors.accent)
    root.style.setProperty('--color-accent-light', colors.accentLight)
    root.style.setProperty('--color-accent-dark', colors.accentDark)
    
    // Base colors
    root.style.setProperty('--color-background', colors.background)
    root.style.setProperty('--color-surface', colors.surface)
    root.style.setProperty('--color-surface-light', colors.surfaceLight)
    
    // Text colors
    root.style.setProperty('--color-text-primary', colors.textPrimary)
    root.style.setProperty('--color-text-secondary', colors.textSecondary)
    root.style.setProperty('--color-text-inverse', colors.textInverse)
    
    // UI colors
    root.style.setProperty('--color-success', colors.success)
    root.style.setProperty('--color-success-dark', colors.successDark)
    root.style.setProperty('--color-success-light', colors.successLight)
    root.style.setProperty('--color-warning', colors.warning)
    root.style.setProperty('--color-warning-dark', colors.warningDark)
    root.style.setProperty('--color-warning-light', colors.warningLight)
    root.style.setProperty('--color-error', colors.error)
    root.style.setProperty('--color-error-dark', colors.errorDark)
    root.style.setProperty('--color-error-light', colors.errorLight)
    root.style.setProperty('--color-info', colors.info)
    root.style.setProperty('--color-info-dark', colors.infoDark)
    root.style.setProperty('--color-info-light', colors.infoLight)
    
    // Border colors
    root.style.setProperty('--color-border', colors.border)
    root.style.setProperty('--color-border-light', colors.borderLight)
    root.style.setProperty('--color-border-strong', colors.borderStrong)
    
    // Additional derived colors (for compatibility)
    root.style.setProperty('--color-primary-50', colors.surfaceLight)
    root.style.setProperty('--color-primary-100', colors.background)
    root.style.setProperty('--color-primary-500', colors.primary)
    root.style.setProperty('--color-primary-600', colors.primaryDark)
    root.style.setProperty('--color-primary-700', colors.primaryDark)
    
    // Update background color
    root.style.setProperty('--color-background-secondary', colors.surfaceLight)
    root.style.setProperty('--color-background-tertiary', colors.background)
    
    // Update link color
    root.style.setProperty('--color-text-link', colors.primary)
    root.style.setProperty('--color-border-focus', colors.primary)
    
    // Surface dark color
    root.style.setProperty('--color-surface-dark', colors.surfaceDark)
    
    // Grade colors (固定)
    root.style.setProperty('--color-grade-a', colors.gradeA)
    root.style.setProperty('--color-grade-b', colors.gradeB)
    root.style.setProperty('--color-grade-c', colors.gradeC)
    root.style.setProperty('--color-grade-d', colors.gradeD)
  }, [palette])

  return (
    <ColorThemeContext.Provider value={{ palette, paletteKey, setPalette }}>
      {children}
    </ColorThemeContext.Provider>
  )
}

// カラーテーマを使用するためのフック
export const useColorTheme = () => {
  const context = useContext(ColorThemeContext)
  if (!context) {
    throw new Error('useColorTheme must be used within a ColorThemeProvider')
  }
  return context
}